import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Select, Switch, Textarea } from '@/components/ui';
import { createManualLead } from '@/data/client';
import type { ManualLeadInput } from '@/data/types';
import { FormError, Modal } from './Modal';
import { OptionSelect } from './OptionSelect';

const CHANNELS = [
  { value: 'phone', label: 'Phone' }, { value: 'email', label: 'Email' },
  { value: 'in_person', label: 'In person' }, { value: 'other', label: 'Other' },
];

// <input type="datetime-local"> speaks local wall-clock time without a zone.
const localNow = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

/* Leads that did not come through digital marketing: cold calls, events,
   personal referrals. They are recorded as source "Outbound" with the specific
   source from the shared list, and stay in clientIQ (never sent to leadIQ). */
export function NewLeadModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [f, setF] = useState<ManualLeadInput>({ channel: 'phone', receivedAt: localNow(), createDeal: false });
  const set = (k: keyof ManualLeadInput) => (e: { target: { value: string } }) => setF((p) => ({ ...p, [k]: e.target.value }));
  const empty = !f.firstName?.trim() && !f.lastName?.trim() && !f.email?.trim() && !f.phone?.trim() && !f.companyName?.trim();

  const save = useMutation({
    mutationFn: () => createManualLead({ ...f, receivedAt: f.receivedAt ? new Date(f.receivedAt).toISOString() : undefined }),
    onSuccess: (r) => {
      for (const key of ['leads', 'leadSummary', 'companies', 'contacts', 'deals']) void qc.invalidateQueries({ queryKey: [key] });
      onClose();
      if (r.dealId) navigate(`/deals/${r.dealId}`);
    },
  });

  return (
    <Modal
      title="Add lead"
      wide
      onClose={onClose}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={() => save.mutate()} disabled={save.isPending || empty || !f.sourceOptionId}>{save.isPending ? 'Saving…' : 'Add lead'}</Button>
      </>}
    >
      <div className="crm-form">
        <div className="crm-grid2">
          <Input label="First name" autoFocus value={f.firstName ?? ''} onChange={set('firstName')} />
          <Input label="Last name" value={f.lastName ?? ''} onChange={set('lastName')} />
          <Input label="Company" value={f.companyName ?? ''} onChange={set('companyName')} />
          <Input label="Email" type="email" value={f.email ?? ''} onChange={set('email')} />
          <Input label="Phone" type="tel" value={f.phone ?? ''} onChange={set('phone')} />
          <Select label="How they reached us" options={CHANNELS} value={f.channel} onChange={set('channel')} />
          <OptionSelect kind="lead_source" label="Source" required value={f.sourceOptionId ?? ''} onChange={(id) => setF((p) => ({ ...p, sourceOptionId: id }))} />
          <Input label="When" type="datetime-local" max={localNow()} value={f.receivedAt ?? ''} onChange={set('receivedAt')} hint="Backdate if logging later — revenue is dated from this." />
        </div>
        <Textarea label="Notes" rows={3} value={f.notes ?? ''} onChange={set('notes')} />
        <Switch label="Create a deal now (first pipeline stage)" checked={!!f.createDeal} onChange={(e) => setF((p) => ({ ...p, createDeal: e.target.checked }))} />
        {f.createDeal && (
          <div className="crm-grid2">
            <Input label="Deal name" placeholder="Defaults to the company name" value={f.dealName ?? ''} onChange={set('dealName')} />
            <Input label="Expected MRR" inputMode="decimal" placeholder="$" value={f.mrr ?? ''} onChange={set('mrr')} />
          </div>
        )}
        <FormError error={save.error} />
      </div>
    </Modal>
  );
}
